/*
 * Author: Roger
 * File Created: Sunday, 24-08-04 04:02 pm
 * Last Modified: Tuesday, 25-04-22 04:44 pm, by Dw
 */

import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import today from 'dayjs/plugin/isToday';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(today);
dayjs.extend(isBetween);
dayjs.extend(timezone);

export const SG_TIMEZONE = 'Asia/Singapore';

type DateType = string | number | Date | dayjs.Dayjs | null;

/**
 * data format version 2.0 based day js
 * @param date date type
 * @param { string } fmt format type default is 'YYYY-MM-DD HH:mm:ss'
 * @param { boolean } isUTC 是否使用 utc 时间
 * @link 更多格式化的信息 👉 https://day.js.org/docs/en/display/format
 * @example
 * format('1994-03-20 09:15:00', 'YYYY')   // '1994'
 * format('1994-03-20 09:15:00', 'YYYY-MM-DD')   // '1994-03-20'
 * format('1994-03-20 09:15:00', 'MMM DD,YYYY HH:mm:ss(a)')   // 'Mar 20,1994 09:15:00(am)'
 * format('1994-03-20 09:15:00', 'MMM/DD/YYYY HH:mm:ss(A)')   // 'Mar/20/1994 09:15:00(AM)'
 * format('1994-03-20 09:15:00', 'MMM DD,YYYY')   // 'Mar 20,1994'
 * format('1994-03-20 09:15:00', 'HH:mm')   // '09:15'
 * format('1994-03-20 09:15:00', 'HH:mm(a)')   // '09:15(am)'
 * format('1994-03-20 09:15:00', 'HH:mm(a) UTCZ')   // '09:15(am) UTC+08:00'
 */
export const dateFormat = (
  _date: DateType = new Date(),
  fmt = 'YYYY-MM-DD HH:mm:ss',
  config: boolean | { isUTC?: boolean; timeZone?: string } = false
): string => {
  let date = _date;

  // 如果时间戳是字符串，则将时间戳自动转成 Number
  if (typeof date === 'string' && /^\d+$/.test(date)) {
    date = Number(date);
  }

  if (typeof config === 'undefined') {
    return dayjs(date).format(fmt);
  }

  if (typeof config === 'boolean' && config) {
    return dayjs(date).utc().format(fmt);
  }

  const { timeZone = SG_TIMEZONE } = config as { timeZone?: string };
  let instance = dayjs(date);
  instance = instance.tz(timeZone || SG_TIMEZONE);
  return instance.format(fmt);
};

export const isToday = (date: DateType = new Date(), isUTC = false): boolean =>
  isUTC ? dayjs(date).utc().isToday() : dayjs(date).isToday();

export const isThisYear = (
  date: DateType = new Date(),
  isUTC = false
): boolean =>
  isUTC
    ? dayjs(date).utc().year() === dayjs().year()
    : dayjs(date).year() === dayjs().year();

export const dateAdd = (
  date: DateType = new Date(),
  value: number,
  unit: dayjs.ManipulateType = 'day',
  isUTC = false
): dayjs.Dayjs =>
  isUTC ? dayjs(date).utc().add(value, unit) : dayjs(date).add(value, unit);

export const dateSubtract = (
  date: DateType = new Date(),
  value: number,
  unit: dayjs.ManipulateType = 'day',
  isUTC = false
): dayjs.Dayjs =>
  isUTC
    ? dayjs(date).utc().subtract(value, unit)
    : dayjs(date).subtract(value, unit);

/**
 * 判断给定时间是否在指定的开始时间和结束时间之间
 * @param targetDate 要判断的时间
 * @param startDate 区间的开始时间
 * @param endDate 区间的结束时间
 * @param isUTC 是否使用 UTC 时间，默认为 false
 * @returns boolean 如果目标时间在区间内返回 true，否则返回 false
 */
export const isDateInRange = (
  targetDate: DateType = new Date(),
  startDate: DateType = new Date(),
  endDate: DateType = new Date(),
  isUTC = false
): boolean => {
  // 使用 dayjs 创建日期对象
  const target = isUTC ? dayjs(targetDate).utc() : dayjs(targetDate);
  const start = isUTC ? dayjs(startDate).utc() : dayjs(startDate);
  const end = isUTC ? dayjs(endDate).utc() : dayjs(endDate);

  // 判断目标时间是否在区间内
  return target.isBetween(start, end, null, '[]'); // '[]' 表示闭区间，包含边界值
};
